import { useState } from "react";
import { motion } from "framer-motion";
import { PILLARS, PRODUCTS, IMAGES } from "@/data/content";
import DeckReveal from "@/components/DeckReveal";

const ease = [0.22, 1, 0.36, 1];

const PillarCard = ({ p, i, onDraw }) => (
  <motion.article
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-12%" }}
    transition={{ duration: 0.9, delay: i * 0.08, ease }}
    onClick={onDraw}
    className={`group glass relative overflow-hidden rounded-2xl transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-2 hover:border-[#C5A059]/60 hover:shadow-[0_0_50px_rgba(59,42,72,0.9)] ${p.span} ${
      onDraw ? "cursor-pointer" : ""
    }`}
    data-testid={`pillar-card-${p.id}`}
  >
    <div className="relative h-64 overflow-hidden md:h-72">
      <img
        src={IMAGES[p.image]}
        alt={p.title}
        className="h-full w-full object-cover opacity-70 transition-[transform,opacity] duration-700 group-hover:scale-105 group-hover:opacity-90"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
      {onDraw && (
        <div className="absolute right-5 top-5 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
          <span className="rounded-full border border-[#C5A059]/60 bg-[#050505]/70 px-4 py-1.5 text-[0.6rem] uppercase tracking-[0.24em] text-[#C5A059] backdrop-blur-md">
            ✦ Draw a card
          </span>
        </div>
      )}
    </div>
    <div className="relative -mt-16 p-8 md:p-10">
      <p className="overline mb-4">{p.tag}</p>
      <h3 className="font-serif text-3xl font-light tracking-tight text-white md:text-4xl">
        {p.title}
      </h3>
      <p className="mt-4 max-w-lg text-base font-light leading-relaxed text-white/55">
        {p.body}
      </p>
    </div>
  </motion.article>
);

export const Ecosystem = () => {
  const [deckOpen, setDeckOpen] = useState(false);
  return (
    <section id="ecosystem" className="relative w-full py-28 md:py-40" data-testid="ecosystem-section">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1, ease }}
          className="mb-16 flex flex-col justify-between gap-6 md:mb-20 md:flex-row md:items-end"
        >
          <div className="max-w-2xl">
            <p className="overline mb-6">The Ecosystem</p>
            <h2 className="font-serif text-[clamp(2.2rem,5vw,4.5rem)] font-light leading-[1.02] tracking-tight text-white">
              Three doorways into the <span className="italic text-[#C5A059]">lineage</span>.
            </h2>
          </div>
          <p className="max-w-sm text-base font-light leading-relaxed text-white/45">
            Objects, circles, and immersions — each a threshold between the woman
            you are and the matriarch you are becoming.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {PILLARS.map((p, i) => (
            <PillarCard
              key={p.id}
              p={p}
              i={i}
              onDraw={p.id === "vault" ? () => setDeckOpen(true) : undefined}
            />
          ))}
        </div>

        {/* The Vault — product relics */}
        <div className="mt-28">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="overline mb-10 text-center"
          >
            Within the Vault
          </motion.p>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {PRODUCTS.map((pr, i) => (
              <motion.div
                key={pr.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.9, delay: i * 0.1, ease }}
                onClick={pr.id === "decks" ? () => setDeckOpen(true) : undefined}
                className={`group relative overflow-hidden rounded-2xl border border-[#C5A059]/15 bg-[#070707] transition-[transform,border-color] duration-500 hover:-translate-y-2 hover:border-[#C5A059]/50 ${
                  pr.id === "decks" ? "cursor-pointer" : ""
                }`}
                data-testid={`product-card-${pr.id}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={IMAGES[pr.image]}
                    alt={pr.title}
                    className="h-full w-full object-cover transition-transform [transition-duration:900ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-80" />
                  {pr.id === "decks" && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <span className="rounded-full border border-[#C5A059]/60 bg-[#050505]/70 px-5 py-2 text-[0.62rem] uppercase tracking-[0.24em] text-[#C5A059] backdrop-blur-md">
                        ✦ Draw a card
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-7">
                  <p className="overline mb-2 text-[0.62rem]">{pr.sub}</p>
                  <h4 className="font-serif text-2xl font-light tracking-tight text-white">
                    {pr.title}
                  </h4>
                  <p className="mt-3 text-sm font-light leading-relaxed text-white/50">
                    {pr.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <DeckReveal open={deckOpen} onClose={() => setDeckOpen(false)} />
    </section>
  );
};

export default Ecosystem;
